import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { object, string } from 'yup';
import { useNavigate, Link } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';

import { SignIn as UserSignIn } from '../../../slices/auth';
import { storeToken } from './utils';
import { getDefaultLandingPath } from '../../../utils/getDefaultLandingPath';
import { hasPendingClientOnboarding } from '../../../utils/onboardingStatus';
import { PERMISSION_MAP } from '../../../constants/routes';

// CSS vars scoped to wrapper so they don't conflict with Tailwind/Bootstrap/AntD
const MKT_VARS = {
  '--mkt-primary':    '#1565D8',
  '--mkt-primary-6':  '#0F4FB3',
  '--mkt-text':       '#0F1729',
  '--mkt-text-2':     '#4E5969',
  '--mkt-text-3':     '#86909C',
  '--mkt-border':     '#E5E6EB',
  '--mkt-danger':     '#F52C50'
};

const GlobalStyles = createGlobalStyle`
  @keyframes mkt-drift {
    0%   { transform: translate3d(0, 0, 0); }
    50%  { transform: translate3d(0, -10px, 0); }
    100% { transform: translate3d(0, 0, 0); }
  }
  @keyframes mkt-spin {
    to { transform: rotate(360deg); }
  }

  /* Reset Bootstrap/AntD/Tailwind interference inside the sign-in page */
  .mkt-signin h1,
  .mkt-signin h2 {
    margin: 0;
    padding: 0;
    font-family: 'Inter', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .mkt-signin p {
    margin: 0;
    padding: 0;
    font-family: 'Inter', system-ui, sans-serif;
  }
  .mkt-signin input,
  .mkt-signin button {
    font-family: 'Inter', system-ui, sans-serif;
    box-sizing: border-box;
  }
  .mkt-signin a { text-decoration: none; }
  .mkt-mono {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
  }

  /* Layout helpers used by responsive overrides */
  .mkt-aside  { display: flex; flex-direction: column; }
  .mkt-main   { display: flex; flex-direction: column; }
  .mkt-mobile-logo { display: none; }

  /* ── Tablet (≤ 960px): compress the brand panel ── */
  @media (max-width: 960px) {
    .mkt-signin  { grid-template-columns: 1fr 1fr !important; }
    .mkt-aside   { padding: 32px 28px !important; }
    .mkt-aside h1 { font-size: 34px !important; line-height: 1.12 !important; letter-spacing: -0.8px !important; }
    .mkt-aside p  { font-size: 14px !important; }
    .mkt-features { gap: 8px !important; }
    .mkt-feature-card { padding: 10px !important; }
    .mkt-main   { padding: 24px 28px 24px !important; }
  }

  /* ── Mobile (≤ 640px): single column, hide brand panel ── */
  @media (max-width: 640px) {
    .mkt-signin      { grid-template-columns: 1fr !important; }
    .mkt-aside       { display: none !important; }
    .mkt-main        { padding: 28px 20px 24px !important; }
    .mkt-mobile-logo { display: flex !important; margin-bottom: 32px; }
    .mkt-form-inner  { padding-bottom: 24px !important; max-width: 100% !important; }
  }

  /* ── Small phones (≤ 390px) ── */
  @media (max-width: 390px) {
    .mkt-main        { padding: 20px 16px 20px !important; }
  }
`;

// ----- Brand pattern: layered geometric SVG -----
function BrandPattern() {
  return (
    <svg
      viewBox="0 0 800 1000"
      preserveAspectRatio="xMidYMid slice"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id="mkt-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0B3E8F" />
          <stop offset="55%" stopColor="#1565D8" />
          <stop offset="100%" stopColor="#1E7AEC" />
        </linearGradient>
        <radialGradient id="mkt-glow1" cx="0.25" cy="0.3" r="0.55">
          <stop offset="0%" stopColor="#4F9BFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#4F9BFF" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="mkt-glow2" cx="0.85" cy="0.85" r="0.6">
          <stop offset="0%" stopColor="#86B9FF" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#86B9FF" stopOpacity="0" />
        </radialGradient>
        <pattern id="mkt-grid" width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M48 0 L0 0 L0 48" fill="none" stroke="#ffffff" strokeOpacity="0.06" strokeWidth="1" />
        </pattern>
        <pattern id="mkt-dots" width="32" height="32" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1" fill="#ffffff" fillOpacity="0.1" />
        </pattern>
      </defs>

      <rect width="800" height="1000" fill="url(#mkt-bg)" />
      <rect width="800" height="1000" fill="url(#mkt-grid)" />
      <rect width="800" height="1000" fill="url(#mkt-glow1)" />
      <rect width="800" height="1000" fill="url(#mkt-glow2)" />

      <circle cx="640" cy="220" r="220" fill="#ffffff" fillOpacity="0.05" />
      <circle cx="640" cy="220" r="140" fill="#ffffff" fillOpacity="0.05" />
      <circle cx="640" cy="220" r="70"  fill="#ffffff" fillOpacity="0.06" />

      <g transform="translate(120 680) rotate(-18)">
        <rect x="-60" y="-60" width="120" height="120" rx="14" fill="#ffffff" fillOpacity="0.06"
          style={{ transformOrigin: 'center', animation: 'mkt-drift 12s ease-in-out infinite' }} />
        <rect x="-40" y="-40" width="80"  height="80"  rx="10" fill="#ffffff" fillOpacity="0.08" />
        <rect x="-22" y="-22" width="44"  height="44"  rx="6"  fill="#ffffff" fillOpacity="0.14" />
      </g>

      <g stroke="#ffffff" strokeOpacity="0.18" strokeWidth="1.5" fill="none">
        <polygon points="300,120 368,160 368,240 300,280 232,240 232,160" />
        <polygon points="300,120 368,160 368,240 300,280 232,240 232,160" transform="translate(40 40) scale(0.7)" />
      </g>

      <g stroke="#ffffff" strokeOpacity="0.08" strokeWidth="1">
        <line x1="-40" y1="820" x2="860" y2="520" />
        <line x1="-40" y1="880" x2="860" y2="580" />
        <line x1="-40" y1="940" x2="860" y2="640" />
      </g>

      <rect x="420" y="560" width="260" height="220" fill="url(#mkt-dots)" opacity="0.7" />

      <g fill="none" stroke="#ffffff" strokeOpacity="0.22" strokeWidth="2">
        <path d="M -60 420 Q 200 340 420 500 T 880 520" />
        <path d="M -60 460 Q 200 380 420 540 T 880 560" strokeOpacity="0.14" />
      </g>
    </svg>
  );
}

// ----- Icon primitives -----
const Icon = ({ size = 18, stroke = 1.75, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const I = {
  Mail:   (p) => <Icon {...p}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></Icon>,
  Lock:   (p) => <Icon {...p}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Icon>,
  Eye:    (p) => <Icon {...p}><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Icon>,
  EyeOff: (p) => <Icon {...p}><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" /><path d="M10.73 5.08A11 11 0 0 1 12 5c6.5 0 10 7 10 7a13.1 13.1 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.39-1.61" /><line x1="2" y1="2" x2="22" y2="22" /></Icon>,
  Check:  (p) => <Icon {...p}><polyline points="20 6 9 17 4 12" /></Icon>,
  Shield: (p) => <Icon {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></Icon>,
  Zap:    (p) => <Icon {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></Icon>,
  Chart:  (p) => <Icon {...p}><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-6" /></Icon>,
  Spin:   ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{ animation: 'mkt-spin 0.8s linear infinite' }}>
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.35)" strokeWidth="2.5" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
};

// ----- Brand wordmark -----
function Wordmark({ light = false, size = 24 }) {
  const color  = light ? '#fff' : 'var(--mkt-text)';
  const accent = light ? '#BED6F7' : 'var(--mkt-primary)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        width: size + 6, height: size + 6, borderRadius: 8,
        background: light ? 'rgba(255,255,255,0.14)' : 'var(--mkt-primary)',
        border: light ? '1px solid rgba(255,255,255,0.3)' : 'none',
        display: 'grid', placeItems: 'center', color: '#fff'
      }}>
        <svg width={size - 6} height={size - 6} viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-6" />
        </svg>
      </div>
      <div style={{ fontSize: size - 2, fontWeight: 700, letterSpacing: -0.4, color }}>
        Market<span style={{ color: accent }}>OS</span>
      </div>
    </div>
  );
}

// ----- Floating-label field -----
function Field({ id, label, type = 'text', value, onChange, onBlur, icon: Ico, error, rightSlot, autoFocus, autoComplete }) {
  const [focus, setFocus] = useState(false);
  const active = focus || (value && value.length > 0);

  return (
    <label htmlFor={id} style={{ display: 'block', position: 'relative' }}>
      <div style={{
        position: 'relative',
        background: '#fff',
        border: `1px solid ${error ? 'var(--mkt-danger)' : focus ? 'var(--mkt-primary)' : 'var(--mkt-border)'}`,
        borderRadius: 10,
        boxShadow: focus ? '0 0 0 4px rgba(21,101,216,0.12)' : error ? '0 0 0 4px rgba(245,44,80,0.08)' : 'none',
        transition: 'box-shadow .15s ease, border-color .15s ease',
        height: 56,
        display: 'flex', alignItems: 'center'
      }}>
        <div style={{
          paddingLeft: 16,
          color: error ? 'var(--mkt-danger)' : focus ? 'var(--mkt-primary)' : 'var(--mkt-text-3)',
          display: 'flex', alignItems: 'center'
        }}>
          <Ico size={18} />
        </div>
        <div style={{ flex: 1, position: 'relative', height: '100%' }}>
          <span style={{
            position: 'absolute', left: 12, right: 12,
            top: active ? 8 : '50%',
            transform: active ? 'translateY(0)' : 'translateY(-50%)',
            fontSize: active ? 11 : 14,
            fontWeight: active ? 600 : 500,
            color: error ? 'var(--mkt-danger)' : active ? (focus ? 'var(--mkt-primary)' : 'var(--mkt-text-3)') : 'var(--mkt-text-3)',
            pointerEvents: 'none',
            transition: 'all .15s ease',
            letterSpacing: active ? 0.3 : 0,
            textTransform: active ? 'uppercase' : 'none',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
          }}>{label}</span>
          <input
            id={id}
            type={type}
            value={value}
            onChange={onChange}
            onBlur={(e) => { setFocus(false); onBlur && onBlur(e); }}
            autoFocus={autoFocus}
            autoComplete={autoComplete}
            onFocus={() => setFocus(true)}
            style={{
              width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent',
              padding: '22px 12px 8px',
              fontSize: 15, fontWeight: 500, color: 'var(--mkt-text)'
            }}
          />
        </div>
        {rightSlot && <div style={{ paddingRight: 10 }}>{rightSlot}</div>}
      </div>
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--mkt-danger)', fontWeight: 500 }}>
          <span style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--mkt-danger)' }} />
          {error}
        </div>
      )}
    </label>
  );
}

// ----- Auth logic helpers -----
const SELLER_PRIORITY = [
  { module: 'inventory',   path: '/inventory/sku' },
  { module: 'purchasing',  path: '/po-history' },
  { module: 'logistics',   path: '/work-orders' },
  { module: 'fileHistory', path: '/file-upload-history' },
  { module: 'settings',    path: '/account' }
];
const WAREHOUSE_PRIORITY = [
  { module: 'dashboard',   path: '/dashboard' },
  { module: 'purchasing',  path: '/po-warehouse' },
  { module: 'logistics',   path: '/warehouse-work-orders' },
  { module: 'location',    path: '/warehouse-location' },
  { module: 'fileHistory', path: '/file-upload-history' },
  { module: 'clients',     path: '/clients' },
  { module: 'settings',    path: '/account' }
];
const SELLER_PLUS_WAREHOUSE_PRIORITY = [
  { module: 'dashboard',   path: '/dashboard' },
  { module: 'inventory',   path: '/inventory/sku' },
  { module: 'purchasing',  path: '/po-history' },
  { module: 'logistics',   path: '/work-orders' },
  { module: 'location',    path: '/warehouse-location' },
  { module: 'fileHistory', path: '/file-upload-history' },
  { module: 'clients',     path: '/clients' },
  { module: 'settings',    path: '/account' }
];

function permissionRedirection(permissions = [], roleId) {
  const userModules = permissions.map(p => PERMISSION_MAP[p]).filter(Boolean);
  const map = { 2: SELLER_PRIORITY, 3: WAREHOUSE_PRIORITY, 5: SELLER_PLUS_WAREHOUSE_PRIORITY };
  const priority = map[roleId];
  if (!priority) return '/access-denied';
  for (const { module, path } of priority) {
    if (userModules.includes(module)) return path;
  }
  return '/access-denied';
}

// ----- Feature cards data -----
const FEATURES = [
  { icon: I.Zap,    label: 'Real-time POs' },
  { icon: I.Chart,  label: 'Live KPIs' },
  { icon: I.Shield, label: 'SOC 2 · Type II' }
];

// ----- Main component -----
const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { signInLoading } = useSelector(state => state?.auth || {});
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);

  const schema = object().shape({
    email:    string().email('Enter a valid email address').required('Email is required'),
    password: string().required('Password is required')
  });

  const handleSignIn = async ({ email, password }) => {
    const response = await dispatch(UserSignIn({ user: { email, password } }));
    if (!response.error) {
      const { jwt, current_user } = response?.payload?.data || {};
      if (jwt) {
        storeToken(jwt, navigate, dispatch);
        if (!current_user.is_employee_of) {
          const roleId = current_user?.role?.id;
          const pendingClientOnboarding = hasPendingClientOnboarding(current_user);
          const businessOnboardingPending =
            !current_user?.business_onboarding_completed && !current_user?.business_onboarding_exited;
          if (roleId === 3) {
            navigate('/warehouse-location');
          } else if ((roleId === 2 || roleId === 5) && pendingClientOnboarding) {
            // Still mid data-sync (or earlier) -> back to the sign-up wizard, not past it.
            navigate('/auth/sign-up');
          } else if ((roleId === 2 || roleId === 5) && businessOnboardingPending) {
            // client onboarding finished but business onboarding not done -> send them there
            navigate('/business-onboarding');
          } else {
            // Seller / seller+warehouse land on the dashboard; other roles on the SKU list.
            navigate(getDefaultLandingPath(current_user));
          }
        } else {
          navigate(permissionRedirection(current_user.permissions, current_user.role.id));
        }
      }
    }
  };

  return (
    <>
      <GlobalStyles />
      <Formik
        validationSchema={schema}
        onSubmit={handleSignIn}
        initialValues={{ email: '', password: '' }}
      >
        {({ handleSubmit, handleChange, handleBlur, values, errors, touched }) => (
          <div
            className="mkt-signin"
            style={{
              ...MKT_VARS,
              minHeight: '100vh', display: 'grid', gridTemplateColumns: '1.05fr 1fr',
              background: '#fff',
              fontFamily: '\'Inter\', system-ui, sans-serif',
              WebkitFontSmoothing: 'antialiased',
              boxSizing: 'border-box'
            }}
          >
            {/* LEFT — brand side */}
            <aside className="mkt-aside" style={{
              position: 'relative', overflow: 'hidden',
              color: '#fff',
              padding: '40px 48px'
            }}>
              <BrandPattern />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Wordmark light size={26} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 520, paddingRight: 24 }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '6px 12px', borderRadius: 999,
                    background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
                    fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
                    width: 'fit-content', marginBottom: 28, backdropFilter: 'blur(8px)'
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#86E4B9', boxShadow: '0 0 10px #86E4B9' }} />
                    All systems operational
                  </div>

                  <h1 style={{
                    fontSize: 48, lineHeight: 1.08, fontWeight: 700, letterSpacing: -1.2,
                    margin: 0, textWrap: 'balance'
                  }}>
                    The operating system for your Amazon operation.
                  </h1>
                  <p style={{
                    marginTop: 20, fontSize: 16, lineHeight: 1.55, color: 'rgba(255,255,255,0.82)',
                    maxWidth: 460, textWrap: 'pretty'
                  }}>
                    Sourcing, Purchase orders, inbound shipments, storage and prep one home, one source of truth. Sign in to pick up where you left off.
                  </p>

                  <div className="mkt-features" style={{
                    marginTop: 36, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
                    gap: 12, maxWidth: 460
                  }}>
                    {FEATURES.map(({ icon: Ico, label }) => (
                      <div key={label} className="mkt-feature-card" style={{
                        display: 'flex', flexDirection: 'column', gap: 8,
                        padding: '14px 14px',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        borderRadius: 12, backdropFilter: 'blur(8px)'
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 8,
                          background: 'rgba(255,255,255,0.14)',
                          display: 'grid', placeItems: 'center'
                        }}>
                          <Ico size={16} />
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  position: 'relative', zIndex: 1,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12, color: 'rgba(255,255,255,0.68)', fontWeight: 500
                }}>
                  <span>© 2026 MarketOS, Inc.</span>
                  <span className="mkt-mono" style={{ letterSpacing: 0.5 }}>v4.12.0</span>
                </div>
              </div>
            </aside>

            {/* RIGHT — form side */}
            <main className="mkt-main" style={{
              padding: '28px 40px 32px',
              position: 'relative'
            }}>
              {/* Logo shown only on mobile (aside is hidden) */}
              <div className="mkt-mobile-logo" style={{ alignItems: 'center' }}>
                <Wordmark light={false} size={24} />
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'contents' }} noValidate>
                <div className="mkt-form-inner" style={{
                  flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  maxWidth: 440, width: '100%', margin: '0 auto', paddingBottom: 40
                }}>
                  <h2 style={{
                    margin: 0, fontSize: 32, fontWeight: 700, letterSpacing: -0.6, color: 'var(--mkt-text)'
                  }}>Welcome back</h2>
                  <p style={{
                    margin: '10px 0 32px', fontSize: 15, color: 'var(--mkt-text-2)', lineHeight: 1.5
                  }}>
                    Sign in with your MarketOS credentials to access your dashboard.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <Field
                      id="email"
                      label="Email address"
                      type="email"
                      icon={I.Mail}
                      value={values.email}
                      onChange={handleChange('email')}
                      onBlur={handleBlur('email')}
                      error={touched.email && errors.email}
                      autoComplete="email"
                      autoFocus
                    />

                    <Field
                      id="password"
                      label="Password"
                      type={showPw ? 'text' : 'password'}
                      icon={I.Lock}
                      value={values.password}
                      onChange={handleChange('password')}
                      onBlur={handleBlur('password')}
                      error={touched.password && errors.password}
                      autoComplete="current-password"
                      rightSlot={
                        <button
                          type="button"
                          onClick={() => setShowPw(s => !s)}
                          aria-label={showPw ? 'Hide password' : 'Show password'}
                          style={{
                            border: 'none', background: 'transparent', color: 'var(--mkt-text-3)',
                            width: 36, height: 36, borderRadius: 8, cursor: 'pointer',
                            display: 'grid', placeItems: 'center'
                          }}
                        >
                          {showPw ? <I.EyeOff size={18} /> : <I.Eye size={18} />}
                        </button>
                      }
                    />

                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginTop: 2, marginBottom: 6
                    }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
                        <span style={{
                          width: 18, height: 18, borderRadius: 5,
                          border: `1.5px solid ${remember ? 'var(--mkt-primary)' : 'var(--mkt-border)'}`,
                          background: remember ? 'var(--mkt-primary)' : '#fff',
                          color: '#fff', display: 'grid', placeItems: 'center',
                          transition: 'all .15s ease'
                        }}>
                          {remember && <I.Check size={12} stroke={3} />}
                        </span>
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                        />
                        <span style={{ fontSize: 13, color: 'var(--mkt-text-2)', fontWeight: 500 }}>Keep me signed in</span>
                      </label>
                      <Link
                        to="/auth/forget-password"
                        style={{ fontSize: 13, color: 'var(--mkt-primary)', fontWeight: 600, textDecoration: 'none' }}
                      >
                        Forgot password?
                      </Link>
                    </div>

                    <button
                      type="submit"
                      disabled={signInLoading}
                      style={{
                        height: 52, borderRadius: 10, border: 'none',
                        cursor: signInLoading ? 'default' : 'pointer',
                        background: signInLoading
                          ? 'var(--mkt-primary-6)'
                          : 'linear-gradient(180deg, #1E7AEC 0%, #1565D8 100%)',
                        color: '#fff', fontSize: 15, fontWeight: 600, letterSpacing: 0.1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        boxShadow: '0 8px 20px rgba(21,101,216,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
                        transition: 'transform .1s ease, box-shadow .15s ease'
                      }}
                      onMouseDown={(e) => { e.currentTarget.style.transform = 'translateY(1px)'; }}
                      onMouseUp={(e)   => { e.currentTarget.style.transform = 'translateY(0)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      {signInLoading ? (
                        <>
                          <I.Spin size={18} />
                          <span>Signing you in…</span>
                        </>
                      ) : (
                        <>
                          <span>Sign in</span>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 12, color: 'var(--mkt-text-3)'
                }}>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Privacy</a>
                    <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Terms</a>
                    <a href="#" style={{ color: 'inherit', textDecoration: 'none' }}>Status</a>
                  </div>
                  <span>Need help? <a href="mailto:info@marketos.io" style={{ color: 'var(--mkt-primary)', fontWeight: 600, textDecoration: 'none' }}>info@marketos.io</a></span>
                </div>
              </form>
            </main>
          </div>
        )}
      </Formik>
    </>
  );
};

export default SignIn;
